import { defineStore } from 'pinia';
import { createPopulateString, parseCollectionData } from '~/utils/strapiComponents/addStrapiData';
import {
  findIfPassesByCountry,
  findIfPassesByCurrency,
  findIfPassesByGroups,
  findIfPassesByStag,
} from '~/utils/strapiComponents/filters';
import { useStoreSsPlayer } from '../softswiss/player';
import { strapi } from '../transport/strapi';
import type { ICrossSalePopupData, ICrossSalePopupStrapiResponse } from '~/types/popups';

const LOCAL_STORAGE_DELAYED_POPUP_KEY = '_cspopup_delayed';

export const useStoreCrossSalePopup = defineStore('crossSalePopup', {
  state: () => ({
    popups: [] as ICrossSalePopupData[],
    delayedPopups: {} as Record<string, boolean>,
    closedPopupsGroupIds: [] as string[],
    requestPromise: null as Promise<ICrossSalePopupStrapiResponse> | null,
  }),

  getters: {
    availablePopupsDelayed(): ICrossSalePopupData[] {
      return this.availablePopups.filter(
        (popup) => !popup.appearDelay || (popup.appearDelay > 0 && this.delayedPopups[popup.id])
      );
    },
    availablePopups(): ICrossSalePopupData[] {
      return this.popups
        .filter((popup) => {
          // unauthorized can't have groups
          if (!useStoreSsPlayer().isPlayerAuthorised) {
            return false;
          }

          // if the writable group is written to the playerGroup, it means that user has closed popup
          if (useStoreSsPlayer().playerGroupIds.includes(popup.writableGroupId)) {
            return false;
          }

          if (this.closedPopupsGroupIds.includes(popup.writableGroupId)) {
            return false;
          }

          const isAllowedByCurrency = findIfPassesByCurrency(
            popup.currencies.includedCurrency || '',
            popup.currencies.excludedCurrency || ''
          );

          if (!isAllowedByCurrency) {
            return false;
          }

          const isAllowedByStag = findIfPassesByStag(
            popup.stags.includedStags || '',
            popup.stags.excludedStags || ''
          );

          if (!isAllowedByStag) {
            return false;
          }

          const isAllowedByCountry = findIfPassesByCountry(
            popup.countries.includedCoutries || '',
            popup.countries.excludedCountries || ''
          );

          if (!isAllowedByCountry) {
            return false;
          }

          const isAllowedByGroups = findIfPassesByGroups(
            popup.groups.includedGroups || '',
            popup.groups.excludedGroups || ''
          );

          if (!isAllowedByGroups) {
            return false;
          }

          return true;
        })
        .sort((popup1, popup2) => popup1.position - popup2.position);
    },

    currentShownPopup(): ICrossSalePopupData | null {
      return this.availablePopupsDelayed.length ? this.availablePopupsDelayed[0] : null;
    },
  },

  actions: {
    async loadCrossSalePopups() {
      if (!this.requestPromise) {
        const populateString = createPopulateString([
          'bannerMobile',
          'bannerDesktop',
          'countries',
          'currencies',
          'groups',
          'stags',
          'action',
        ]);

        this.requestPromise = strapi()
          .get('cross-sale-popups', populateString)
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        const parsedData = parseCollectionData(response.data);
        const filteredState = this.popups.filter(
          (item) => !parsedData.find(({ id }) => id && id === item.id)
        );

        this.popups = [...filteredState, ...parsedData].sort(
          (a, b) => Number(a.position) - Number(b.position)
        );

        return true;
      } else {
        return false;
      }
    },
    async syncPopups() {
      await Promise.all(
        this.availablePopups.map(async (popup) => {
          if (popup.appearDelay && popup.appearDelay > 0) {
            if (!localStorage.getItem(`${LOCAL_STORAGE_DELAYED_POPUP_KEY}_${popup.id}`)) {
              localStorage.setItem(
                `${LOCAL_STORAGE_DELAYED_POPUP_KEY}_${popup.id}`,
                new Date(new Date().getTime() + popup.appearDelay * 1000).toUTCString()
              );
            }

            const delayedPopupDate = new Date(
              localStorage.getItem(`${LOCAL_STORAGE_DELAYED_POPUP_KEY}_${popup.id}`) as string
            );

            if (delayedPopupDate > new Date()) {
              await (() =>
                new Promise((resolve) =>
                  setTimeout(resolve, delayedPopupDate.getTime() - new Date().getTime())
                ))();

              this.delayedPopups = { ...this.delayedPopups, [popup.id]: true };
            } else {
              this.delayedPopups = { ...this.delayedPopups, [popup.id]: true };
            }
          }
        })
      );
    },
    /*
      when popup is closed the main idea is to add user group from writable_group_id
      popup field. As a rule, this field will be in excluded_groups of popup as well,
      so after update it won't appear for user
    */
    async closePopup() {
      if (this.currentShownPopup) {
        await (async () => {})();
        const removeGroup = this.currentShownPopup.writableGroupId;
        this.closedPopupsGroupIds.push(removeGroup);
        await useStoreSsPlayer().updatePlayerGroups({ add: [removeGroup] });
      }
    },
  },
});
